import { ChangeDetectionStrategy, Component, ViewChild, ChangeDetectorRef, NgZone, OnInit, OnDestroy } from '@angular/core';
import * as _ from 'lodash';
import { Router, ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';

import { BrandService } from 'shared/brand/brand.service';
import { ProcessService } from 'shared/process/process.service';
import { ScrollToService } from 'shared/base/scroll-to.service';
import { SearchPageService } from 'shared/search-page/search-page.service';
import { SavedSearchesService } from 'shared/saved-searches/saved-searches.service';
import { UserService } from 'shared/user/user.service';

import { SearchPageComponent } from 'shared/search-page/search-page.component';
import { SearchPageSimpleHeaderComponent } from 'shared/search-page/search-page-simple-header/search-page-simple-header.component';
import { ListManagerService } from 'shared/list-manager/list-manager.service';
import { ErrorModalComponent } from 'shared/modal/error-modal/error-modal.component';

import { AppRoutingService } from '../app-routing.service';

import { PermissionsService } from 'shared/permissions/permissions.service';
import { ReturnRouteService } from 'shared/return-route/return-route.service';
import { SearchModel } from 'shared/search/search.model';

import { columns } from './search/search-results-columns';
import { ImportManagerSearchService } from './search/import-manager-search.service';
import { importExportPagesData } from 'utils/import-export-pages-data';
import { PreferencesService } from 'shared/preferences/preferences.service';

import { ReportModalComponent } from './component/report-modal/report-modal.component';
import { ImportUploadModalComponent } from './component/import-upload-modal/import-upload-modal.component';
import { actions } from './import-manager-actions';
import {takeWhile} from 'rxjs/operators';
import {ProductPanelComponent} from '../product-panel/product-panel.component';


@Component({
  moduleId: module.id,
  selector: 'sd-import-manager',
  templateUrl: 'import-manager.component.html',
  styleUrls: ['import-manager.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ImportManagerComponent extends SearchPageComponent implements OnInit, OnDestroy {

  contentSelector: string = '#import-manager-content';
  rowsHover: boolean = true;
  searchInputPlaceholder: string = 'Search for an import file by name...';
  pagesMenuData = importExportPagesData;
  incomplete: Array<string> = [];
  intervalId: any = null;
  actions: any = actions;
  ghostValue: string = 'import-manager';

  timezone: any;
  viewReportItem: any; // item for view report modal
  showSidedrawer: boolean = true; // use this field to show sidedrawer

  @ViewChild(ReportModalComponent, {static: false}) reportModal: ReportModalComponent | void;

  @ViewChild(ImportUploadModalComponent, {static: false}) importUploadModal: ImportUploadModalComponent | void;

  @ViewChild(SearchPageSimpleHeaderComponent, {static: false}) declare headerEl: SearchPageSimpleHeaderComponent | void;

  constructor(
    public searchService: ImportManagerSearchService,
    protected brandService: BrandService,
    protected router: Router,
    protected route: ActivatedRoute,
    protected pageService: SearchPageService,
    protected processService: ProcessService,
    protected scrollToService: ScrollToService,
    protected appRoutingService: AppRoutingService,
    protected permissionsService: PermissionsService,
    protected ngZone: NgZone,
    protected ref: ChangeDetectorRef,
    protected returnRouteService: ReturnRouteService,
    protected location: Location,
    protected preferencesService: PreferencesService,
    protected savedSearchesService: SavedSearchesService,
    protected userService: UserService,
  ) {
    super(
      searchService, brandService, pageService, processService, scrollToService,
      appRoutingService, null, permissionsService, ngZone, ref, route, location,
      savedSearchesService, router, userService,
    );
    this.searchResultsColumns = columns;
  }

  /*******************************************
   * COMPONENT HOOKS
   *******************************************/

  ngOnInit() {
    const pageData: any = _.find(this.pagesMenuData, {name: 'import-manager'});
    pageData.active = true;

    this.searchRanSubj
    .pipe(takeWhile(() => this.isPageActive))
    .subscribe((results: any) => {
      this.incomplete = [];
      if (results.data && results.data.length) {
          _.forEach(results.data, (row) => {
            if (!row.download.completed) {
              this.incomplete.push(row.download.file_id);
            }
          });
      }
    });

    // get timezone
    this.preferencesService.getPreferences()
    .subscribe((preferences: any) => {
      this.timezone = preferences.timezone;
    });

    super.ngOnInit();

    this.intervalId = setInterval(() => {
      this.searchService.getCompleted(this.brandService.getBrand(), this.incomplete)
      .pipe(takeWhile(() => this.isPageActive && !!this.incomplete.length))
      .subscribe((completed) => {

        _.forEach( _.filter(this.searchService.searchResults, ['download.completed', false]), (result) => {
            const completedResult: any = _.find(completed, (c) => {
              return c.id === result.id && c.download.completed === true;
            });
            if (completedResult) {
              result.parts_import_id = completedResult.parts_import_id;
              result.type = completedResult.type;
              result.import_type = completedResult.import_type;
              result.item_count = completedResult.item_count;
              result.app_count = completedResult.app_count;
              result.download = completedResult.download;
              result.report = completedResult.report;
              _.pull(this.incomplete, result.id);
            }
        });
        this.ref.markForCheck();
      });
    }, 10000);
  }

  ngOnDestroy() {
    if (this.intervalId) {
      this.intervalId = clearInterval(this.intervalId);
    }
    super.ngOnDestroy();
  }

  /*******************************************
   * COMPONENT GETTERS
   *******************************************/

  get selectedRowsSize(): number {
    // if selected all items return total records length
    if (this.allSelected) {
      return this.totalRecords;
    }
    // if there is no sort by status return all selected rows length
    return this.selectedRows.length;
  }

  // implemented
  get storedSearchModel(): SearchModel | null {
    if (_.has(this, 'user.id')) {
      return JSON.parse(localStorage.getItem(`importManagerSearchModel.${this.brandService.getBrand()}.${this.user.id}`));
    } else {
      return null;
    }
  }

  // implemented
  get headerColor(): string {
    return 'gold';
  }

  get localStorageName(): string {
    if (_.has(this, 'user.id')) {
      return `importManagerSearchModel.${this.brandService.getBrand()}.${this.user.id}`;
    }
    return null;
  }

  get searchInputValue(): string {
    // handle it in the components
    // to get search value from header search input
    return this.headerEl && this.headerEl.searchInputValue;
  }

  /*******************************************
   * COMPONENT PUBLIC METHODS
   *******************************************/

  // implemented
  storeSearchModel(searchModel: SearchModel) {
    if (_.has(this, 'user.id')) {
      localStorage.setItem(this.localStorageName, JSON.stringify(searchModel));
    }
  }

  removeSearchModel() {
    if (_.has(this, 'user.id')) {
      localStorage.removeItem(this.localStorageName);
    }
  }

  /*******************************************
   * COMPONENT HANDLERS
   *******************************************/

  onSearchCall() {
    if (this.searchModel) {
      this.searchModel.currentPage = 1;
      if (this.searchForm) {
        this.searchModel.searchValue = this.searchForm.searchTermValue;
      }
      this._searchModel.next(this.searchModel);
    }
    this.searchService.toggleForm(false);
  }

  onCancelClick() {
    if (this.searchForm) {
      this.searchForm.resetValue(this.searchModel && this.searchModel.searchValue || '');
    }
    this.searchService.toggleForm(false);
  }

  onViewReportClick(row: any) {
    this.viewReportItem = row;
    if (this.reportModal) {
      this.reportModal.show();
    }
  }

  onFileUploaded() {
    // clear all search settings
    // it'll be save in localStorage automatically
    this.searchModel = new SearchModel();
    this._searchModel.next(this.searchModel);
  }

  onPageActionSelect(event: any) {
    if (event.action === 'upload' && this.importUploadModal) {
      this.importUploadModal.show();
    }
  }

  onLeftTabClick() {
    this.router.navigate(['/export-manager']);
  }

  onAdvancedSearchRun() {
    if (this.searchModel instanceof SearchModel) {
      this.searchModel.currentPage = 1;
    }
    super.onAdvancedSearchRun();
  }
}
